import {
  InlineCitation,
  InlineCitationText,
  InlineCitationCard,
  InlineCitationCardTrigger,
  InlineCitationCardBody,
  InlineCitationCarousel,
  InlineCitationCarouselHeader,
  InlineCitationCarouselIndex,
  InlineCitationCarouselPrev,
  InlineCitationCarouselNext,
  InlineCitationCarouselContent,
  InlineCitationCarouselItem,
  InlineCitationSource,
} from "@altimateai/lego";
import { Citation } from "../../../../components/teammate/types";

interface CitationsProps {
  citations?: Citation[];
  frontEndUrl: string;
  text?: string;
}

/**
 * Citations - AI SDK Elements version of Citations
 *
 * Renders inline citation pills with hover popover showing citation details.
 * Maintains API compatibility with the legacy Citations component while using
 * AI SDK Elements InlineCitation components.
 *
 * @example
 * ```tsx
 * <Citations
 *   citations={[
 *     { id: "1", content: "Learn about React hooks", taskLabel: TaskLabels.DocGen },
 *     { id: "2", content: "State management patterns", taskLabel: TaskLabels.DocGen }
 *   ]}
 *   frontEndUrl="https://example.com"
 *   text="React uses hooks for state management"
 * />
 * ```
 */
const Citations = ({
  citations,
  frontEndUrl,
  text = "Learnings applied",
}: CitationsProps): JSX.Element | null => {
  if (!citations?.length || !frontEndUrl) {
    return null;
  }

  // Convert citations to source URLs for the InlineCitationCardTrigger
  const sources = citations.map(
    (citation) =>
      `${frontEndUrl}/teammates/${citation.taskLabel}?learning=${citation.id}`,
  );

  return (
    <InlineCitation>
      <InlineCitationText>{text}</InlineCitationText>
      <InlineCitationCard>
        <InlineCitationCardTrigger sources={sources} />
        <InlineCitationCardBody>
          <InlineCitationCarousel>
            <InlineCitationCarouselHeader>
              <InlineCitationCarouselPrev />
              <InlineCitationCarouselIndex />
              <InlineCitationCarouselNext />
            </InlineCitationCarouselHeader>
            <InlineCitationCarouselContent>
              {citations.map((citation, index) => {
                const url = `${frontEndUrl}/teammates/${citation.taskLabel}?learning=${citation.id}`;
                return (
                  <InlineCitationCarouselItem key={citation.id}>
                    <InlineCitationSource
                      title={`Learning ${index + 1}`}
                      url={url}
                      description={citation.content}
                    />
                  </InlineCitationCarouselItem>
                );
              })}
            </InlineCitationCarouselContent>
          </InlineCitationCarousel>
        </InlineCitationCardBody>
      </InlineCitationCard>
    </InlineCitation>
  );
};

export default Citations;
